/**
 * API Service - Handles all backend API calls
 * Base URL: http://44.248.45.89:8000
 */

import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:' ? `${window.location.protocol}//${window.location.host}` : `http://${window.location.hostname}:8000`);

// Create axios instance with default config.
// timeout: axios defaults to 0 (wait forever). A single stalled connection then
// hangs the request indefinitely — which is exactly what turned a transient DB
// lock pile-up into an infinite loading spinner. A 60s ceiling guarantees every
// request eventually settles; genuinely long calls (PDF/AI/export) can override
// per-request with a larger `timeout`. The auth bootstrap uses a tighter timeout
// (see App.js) so the app shell never hangs on startup.
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// A 422 from FastAPI is a LIST of objects, not a string. Handed straight to
// setError() and rendered, React throws "Objects are not valid as a React
// child" — and with no global ErrorBoundary that blanks the entire page.
// That is what a failed photo upload did: a black screen instead of "that
// did not save". Every error path should put its detail through here.
export const errorText = (e, fallback = 'Something went wrong.') => {
  const d = e?.response?.data?.detail ?? e?.response?.data ?? e;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const parts = d.map((x) => {
      if (typeof x === 'string') return x;
      const where = Array.isArray(x?.loc) ? x.loc[x.loc.length - 1] : null;
      return [where, x?.msg].filter(Boolean).join(': ');
    }).filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if (d && typeof d === 'object') {
    if (typeof d.detail === 'string') return d.detail;
    if (typeof d.message === 'string') return d.message;
  }
  return (typeof e?.message === 'string' && e.message) || fallback;
};

const getStoreSessionId = () => {
  let sessionId = localStorage.getItem('store_session_id');
  if (!sessionId) {
    sessionId = `sess_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem('store_session_id', sessionId);
  }
  return sessionId;
};

// Is the session we are holding a "view as" one?
//
// It matters to the refresh path. An impersonation token is minted for the
// whole 30 minutes the session is ever allowed to last, so it NEVER needs
// refreshing — and refreshing it is actively wrong: the refresh_token in the
// browser still belongs to the administrator, so the renewed access token
// comes back as THEM. The identity guard below then sees the subject change
// and ends the session, which is why "View as" signed people out.
const _isImpersonating = (tok) => {
  const t = tok || localStorage.getItem('token');
  if (!t) return false;
  try {
    return Boolean(JSON.parse(atob(t.split('.')[1])).imp);
  } catch { return false; }
};

export const setAuthToken = (token, refreshToken) => {
  if (token) {
    localStorage.setItem('token', token);
  }
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
};

export const clearAuthToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  // THE CACHED PROFILE AND THE STASHED ADMIN TOKEN GO TOO.
  //
  // "when i log in i log in as mobile trainer" — because they did not. The
  // tokens were cleared and `user` was left behind, so the application went
  // on rendering the name, role and permissions of whoever was last in this
  // browser until a fresh profile replaced them. And `impersonator_token`
  // outliving the session meant a later, perfectly ordinary session could
  // still be read as a view-as one.
  localStorage.removeItem('user');
  localStorage.removeItem('impersonator_token');
};

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  // A FORM POST MUST NOT CARRY THE JSON DEFAULT.
  // The instance sets Content-Type: application/json for everything, so a
  // FormData body went out with no multipart boundary: FastAPI parsed no
  // fields at all and answered 422 "Field required" for every one of them.
  // Deleting the header here lets the browser write its own boundary, and
  // fixes it for every caller rather than the ones that remembered to
  // override it by hand.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
    if (config.headers.common) delete config.headers.common['Content-Type'];
  }
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const companyId = localStorage.getItem('selectedCompanyId');
  const labId = localStorage.getItem('selectedLabId');
  // Which client practice a multi-practice login is acting as. The
  // server only ever narrows WITHIN what that login is attached to, so
  // an unrecognised value is ignored rather than obeyed.
  const clientId = localStorage.getItem('selectedClientId');
  if (companyId) config.headers['X-Company-Id'] = companyId;
  if (clientId) config.headers['X-Client-Id'] = clientId;
  // '__all__' is the sentinel for "user explicitly chose system-wide view".
  // Don't send it as a header — the backend would either fail UUID parse or
  // worse, treat the literal string as a lab id and filter to zero rows.
  if (labId && labId !== '__all__') config.headers['X-Lab-Id'] = labId;
  return config;
});

// ─── Silent refresh coordination ────────────────────────────────────────
// When an access token expires, we call /auth/refresh to get a new one.
// Concurrent requests that fail with 401 share the same in-flight refresh
// so we only hit /auth/refresh once per expiry event.
let _refreshInFlight = null;
let _proactiveRefreshTimer = null;

const _decodeJwtExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null; // epoch ms
  } catch {
    return null;
  }
};

const _jwtSubject = (token) => {
  try {
    return JSON.parse(atob(String(token).split('.')[1])).sub ?? null;
  } catch {
    return null;
  }
};

/* A REFRESH THAT COULD NOT REACH THE SERVER IS NOT A DEAD SESSION.
   The API restarts on every deploy and is unreachable for ten or twenty
   seconds. A refresh that lands in that window used to fail, and the caller
   threw the session away — signing people out in the middle of their work,
   with a form half filled in, for no reason but a deploy.
   So a request that got NO ANSWER is retried across that window. A request
   the server ANSWERED with a refusal is not retried: that session really is
   over, and pretending otherwise just delays the sign-in screen. */
const _REFRESH_BACKOFF_MS = [1500, 3000, 6000];

const _postRefresh = async (refreshToken, attempt = 0) => {
  try {
    return await axios.post(`${API_BASE_URL}/auth/refresh`,
                            { refresh_token: refreshToken });
  } catch (e) {
    const answered = !!e?.response;
    const serverDown = !answered || e.response.status >= 500;
    if (serverDown && attempt < _REFRESH_BACKOFF_MS.length) {
      await new Promise(r => setTimeout(r, _REFRESH_BACKOFF_MS[attempt]));
      return _postRefresh(refreshToken, attempt + 1);
    }
    throw e;
  }
};

// THE LAST TIME ANY TAB ROTATED, shared through localStorage.
//
//   "i hate that you log me out in the middle of my work"
//
// Two tabs of the same laboratory share one localStorage and schedule their
// proactive refresh off the SAME token expiry, so they fire within the same
// second. /auth/refresh ROTATES — the token presented is revoked — so the
// loser of that race presents a token the winner has just killed, and the
// server read that as theft and revoked every refresh token the person held:
// signed out of every tab and every device, mid-shift. Production shows 17 of
// one account's tokens revoked in a single second.
//
// The server now forgives a token re-presented within a minute of its own
// rotation, which is the real fix. This stops the race happening at all:
// if another tab rotated a moment ago and left a token that is still good,
// use it instead of presenting the one it revoked.
const _REFRESH_STAMP = 'token_refreshed_at';
const _ROTATED_RECENTLY_MS = 15 * 1000;

const _tokenAnotherTabJustLeft = () => {
  try {
    const stamp = Number(localStorage.getItem(_REFRESH_STAMP) || 0);
    if (!stamp || Date.now() - stamp > _ROTATED_RECENTLY_MS) return null;
    const current = localStorage.getItem('token');
    const expMs = _decodeJwtExp(current);
    // Only if it has real life left — a token about to expire is no use.
    return (expMs && expMs - Date.now() > 60 * 1000) ? current : null;
  } catch { return null; }
};

const _refreshAccessToken = () => {
  if (_refreshInFlight) return _refreshInFlight;
  const fresh = _tokenAnotherTabJustLeft();
  if (fresh) {
    try { scheduleProactiveRefresh(fresh); } catch { /* timer only */ }
    return Promise.resolve(fresh);
  }
  // A view-as token already covers the entire window the session may last.
  // Renewing it with the administrator's refresh_token would hand back the
  // ADMINISTRATOR's identity — the exact swap the guard below refuses, which
  // is how this ended in a sign-out instead of a banner.
  if (_isImpersonating()) {
    return Promise.reject(new Error('impersonation_session_is_not_refreshable'));
  }
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.reject(new Error('no_refresh_token'));
  _refreshInFlight = _postRefresh(refreshToken)
    .then((resp) => {
      const newAccess = resp.data?.access_token;
      if (!newAccess) throw new Error('refresh_response_missing_access_token');
      // A refresh renews a session; it must never change WHO you are. A stale
      // refresh_token left behind by a previous sign-in used to swap the
      // session to that account silently — one screen 403ing as the old
      // identity while everything else ran as the new one. If the subject
      // moves, the session is inconsistent: end it and make them sign in.
      const was = _jwtSubject(localStorage.getItem('token'));
      const now = _jwtSubject(newAccess);
      if (was && now && was !== now) {
        clearAuthToken();
        throw new Error('refresh_identity_mismatch');
      }
      localStorage.setItem('token', newAccess);
      // THE REFRESH TOKEN IS ROTATED, AND THE NEW ONE HAS TO BE KEPT.
      //
      // /auth/refresh calls validate_refresh_token(..., rotate=True): the
      // token just presented is REVOKED and a replacement is returned. This
      // stored only the access token, so the browser went on holding the
      // revoked one — and the NEXT refresh presented a revoked token, which
      // the server correctly reads as token reuse.
      //
      // That is why people were signed out "for no reason" in the middle of
      // working: not a deploy, not idleness, just the second refresh of the
      // session. Roughly one access-token lifetime after the first.
      if (resp.data?.refresh_token) {
        localStorage.setItem('refresh_token', resp.data.refresh_token);
      }
      // Tell the other tabs, so they do not present the token just revoked.
      try { localStorage.setItem(_REFRESH_STAMP, String(Date.now())); } catch { /* private mode */ }
      scheduleProactiveRefresh(newAccess);
      return newAccess;
    })
    .finally(() => {
      _refreshInFlight = null;
    });
  return _refreshInFlight;
};

/**
 * Schedule a silent refresh ~60 seconds BEFORE the access token expires.
 * Called on app boot and after every successful refresh.
 */
export const scheduleProactiveRefresh = (accessToken) => {
  if (_proactiveRefreshTimer) {
    clearTimeout(_proactiveRefreshTimer);
    _proactiveRefreshTimer = null;
  }
  const token = accessToken || localStorage.getItem('token');
  if (!token) return;
  if (!localStorage.getItem('refresh_token')) return; // nothing to refresh with
  if (_isImpersonating(token)) return;                // ends on its own, by design
  const expMs = _decodeJwtExp(token);
  if (!expMs) return;
  const leadTimeMs = 60 * 1000; // refresh 60s before expiry
  // A few seconds of spread, so two tabs reading the SAME expiry do not fire
  // in the same instant. With the stamp above, the second one then finds the
  // first one's token already in place and makes no request at all.
  const jitterMs = Math.floor(Math.random() * 20 * 1000);
  const delay = Math.max(5 * 1000, expMs - Date.now() - leadTimeMs - jitterMs);
  _proactiveRefreshTimer = setTimeout(() => {
    _refreshAccessToken().catch(() => {
      // If refresh fails (token revoked, server down), the next API call will
      // hit 401 and the response interceptor will surface the auth:unauthorized event.
    });
  }, delay);
};

// Kick it off on module load so a page reload gets proactive refresh too.
if (typeof window !== 'undefined') {
  setTimeout(() => {
    try { scheduleProactiveRefresh(); } catch {}
  }, 0);

  // Browsers throttle setTimeout in hidden tabs (often to once/min), so the
  // scheduled proactive refresh may miss its window when the tab is in the
  // background. When the tab becomes visible again, do a synchronous check
  // and refresh if the current access token is near or past expiry — this
  // prevents the "come back → click → 401 → redirect to login" flow.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const token = localStorage.getItem('token');
    const refresh = localStorage.getItem('refresh_token');
    if (!token || !refresh) return;
    const expMs = _decodeJwtExp(token);
    if (!expMs) return;
    // If the token is within 2 minutes of expiry (or already expired), refresh now.
    if (expMs - Date.now() < 2 * 60 * 1000) {
      _refreshAccessToken()
        .then(() => { try { scheduleProactiveRefresh(); } catch {} })
        .catch(() => {}); // the next API call will surface the unauthorized event
    } else {
      // Tab was backgrounded — make sure our timer is still scheduled correctly.
      try { scheduleProactiveRefresh(token); } catch {}
    }
  });

  // When another tab refreshes the token, sync it into this tab so we don't
  // keep using a stale access token.
  window.addEventListener('storage', (e) => {
    if (e.key === 'token' && e.newValue) {
      try { scheduleProactiveRefresh(e.newValue); } catch {}
    }
  });
}

// Handle auth errors + silent refresh
// ── Session keepalive ──────────────────────────────────────────────────────
// The server times out a session after 30 minutes measured from the last
// authenticated request. Typing and reading are not requests, so someone
// filling in a long form — a new client, a patient wizard — looks idle to the
// server and gets logged out mid-sentence, losing what they had entered.
//
// This corrects what counts as activity rather than weakening the timeout: a
// ping goes out at most every few minutes, and ONLY if there has been real
// interaction since the last one. A session left genuinely untouched sends
// nothing and still expires, which is the behaviour the control exists for.
(() => {
  // Three minutes against a ten-minute server window. At five it took only two
  // consecutive failures — one flaky moment on hospital wifi — to cross the
  // threshold while someone was actively working. Three gives three chances.
  const PING_EVERY_MS = 3 * 60 * 1000;
  let sawActivity = false;

  const note = () => { sawActivity = true; };
  ['keydown', 'mousedown', 'touchstart', 'scroll'].forEach(evt =>
    window.addEventListener(evt, note, { passive: true, capture: true }));

  setInterval(() => {
    if (!sawActivity) return;              // genuinely idle — let it expire
    if (!localStorage.getItem('token')) return;
    if (document.visibilityState === 'hidden') return;  // tab in the background
    sawActivity = false;
    // AND TAKE THE RENEWED TOKEN WHEN ONE COMES BACK.
    //
    //   "stop logging people out ... the token is good for 20 hours"
    //
    // A JWT's expiry is fixed when it is issued, so a longer configured
    // lifetime does nothing for a token already sitting in this browser, and
    // even a fresh one eventually throws out whoever is still mid-task when
    // it lapses. Past halfway the server hands back a re-stamped token; the
    // session then lasts as long as somebody keeps working, and idleness —
    // not the clock — is what ends it.
    //
    // Failure is deliberately silent: a keepalive that pops an error dialog
    // is worse than the logout it prevents. A real 401 is handled below.
    api.get('/auth/session/ping')
      .then(r => {
        const fresh = r?.data?.token;
        // Only ever REPLACE a token, never create one: if the user signed
        // out in another tab while this request was in flight, writing the
        // key back would silently sign them in again.
        if (fresh && localStorage.getItem('token')) {
          localStorage.setItem('token', fresh);
        }
      })
      .catch(() => {});
  }, PING_EVERY_MS);
})();

// A 200 that is actually the login page.
//
// On a custom domain, nginx decides between the API and the SPA for shared
// paths like /patients by whether the request carries an Authorization
// header. When the session expires the token is cleared from localStorage,
// the request interceptor above stops attaching the header, and nginx hands
// the XHR **index.html with status 200**. Axios reports success, the page
// does `Array.isArray(res.data) ? … : []`, and the screen renders as EMPTY
// rather than signed out — which is how a laboratory came to believe its
// patients had been deleted.
//
// So: an HTML body where JSON was expected is not data. It is the session
// being over, and it is reported as such.
const looksLikeTheAppShell = (response) => {
  const ct = String(response?.headers?.['content-type'] || '');
  if (!ct.includes('text/html')) return false;
  const asked = String(response?.config?.responseType || 'json');
  if (asked === 'blob') {
    /* A BLOB CAN BE THE SHELL TOO, and it is the worst case: the bytes get
     * saved to disk with whatever extension the caller chose. An invoice
     * downloaded this way was 919 bytes of index.html called
     * SAAS-2026-10-0001.pdf, and the only symptom was "Failed to load PDF
     * document" in a viewer with nothing to say about why.
     *
     * The old comment here said "blobs/text are fine". They are not. */
    const t = String(response?.data?.type || '');
    return t.includes('text/html');
  }
  if (asked !== 'json' && asked !== '') return false;   // text is fine
  return typeof response.data === 'string'
      && /<\/?(!doctype|html)\b/i.test(response.data.slice(0, 200));
};

api.interceptors.response.use(
  (response) => {
    if (looksLikeTheAppShell(response)) {
      const err = new Error(
        'Your session has ended. Sign in again to continue.');
      err.sessionExpired = true;
      err.response = { status: 401, data: { detail: err.message } };
      err.config = response.config;
      return Promise.reject(err);
    }
    return response;
  },
  async (error) => {
    /* A WRITE THAT NEEDS A FRESH SECOND FACTOR ASKS FOR ONE AND RETRIES.
     *
     * Platform writes — charging a card, voiding an invoice, re-pointing
     * the address invoices are signed with — answer 401
     * {error: "step_up_required"} when the last proof of possession is
     * older than the window. Showing that as a raw error would mean the
     * operator loses what they were doing and has to find the security
     * page, so instead the prompt comes to them: a code box, then the
     * original request is sent again exactly as it was.
     *
     * Nothing retries silently. The prompt is a person typing a code. */
    try {
      const d401 = error?.response?.data?.detail;
      if (error?.response?.status === 401 && d401
          && typeof d401 === 'object' && d401.error === 'step_up_required'
          && !error.config?._steppedUp) {
        const code = await new Promise((resolve) => {
          window.dispatchEvent(new CustomEvent('lis:step-up-required', {
            detail: { message: d401.message, resolve },
          }));
        });
        if (code) {
          error.config._steppedUp = true;      // once only, never a loop
          return api.request(error.config);
        }
      }
    } catch (_e) { /* fall through to the normal error path */ }

    /* AN IDLE PLATFORM SESSION IS NOT A DEAD ONE. Corporate billing holds
     * itself to a shorter idle window than the rest of the application, so
     * this 401 means "sign in again to open the books", not "your session
     * is over" — the rest of the app is still perfectly usable, and
     * throwing the whole session away would be a worse outage than the
     * rule is worth. */
    try {
      const dp = error?.response?.data?.detail;
      if (error?.response?.status === 401 && dp && typeof dp === 'object'
          && dp.error === 'platform_session_expired') {
        error.response.data.detail = dp.message
          || 'This platform session has been idle too long. Sign in again '
             + 'to open corporate billing.';
        error.platformSessionExpired = true;
        error.doNotClearSession = true;
      }
    } catch (_e) { /* fall through */ }

    /* A `detail` THAT IS NOT A STRING IS A WHITE SCREEN WAITING TO HAPPEN.
     *
     *   "why the error?" — Minified React error #31, keys
     *   {type, loc, msg, input, url}
     *
     * FastAPI answers a validation failure with 422 and `detail` as a LIST OF
     * OBJECTS. Across this app there are 829 places that do
     * `setError(err.response?.data?.detail || '...')` and then render it, and
     * React cannot render an object as a child — so instead of showing the
     * message, the page dies. The moment the message matters most is the
     * moment it is destroyed.
     *
     * Normalised HERE, once, at the only point every one of those 829 call
     * sites has in common. The original is kept on `detail_raw` for anything
     * that wants the structure. */
    try {
      const d = error?.response?.data?.detail;
      if (d && typeof d !== 'string') {
        error.response.data.detail_raw = d;
        if (Array.isArray(d)) {
          const parts = d.map((e) => {
            const where = Array.isArray(e?.loc)
              ? e.loc.filter((x) => x !== 'body').join('.') : '';
            return [where, e?.msg].filter(Boolean).join(': ');
          }).filter(Boolean);
          error.response.data.detail = parts.length
            ? parts.join(' · ') : 'The request was not valid.';
        } else {
          error.response.data.detail =
            d.message || d.msg || d.error || JSON.stringify(d);
        }
      }
    } catch (_normaliseFailed) {
      /* Never let tidying an error become a second error. */
    }

    const original = error.config || {};
    const status = error.response?.status;

    // Don't try to refresh the refresh endpoint itself (avoid infinite loop)
    const url = (original.url || '').toLowerCase();
    const isAuthCall = url.includes('/auth/refresh') || url.includes('/auth/login');

    // WRONG DOOR: a patient account reached a laboratory endpoint.
    //
    // /login authenticates against the staff table, and some rows there are
    // patients from before the portal existed. The server now refuses them on
    // laboratory endpoints — but refusing without moving them just leaves a
    // dead screen. Send them to the door their credentials actually open.
    //
    // Keyed on the server's own marker rather than a bare 403, because plenty
    // of legitimate 403s (a chart you may not open, a PIN not yet entered)
    // must NOT bounce anyone out of the page they are on.
    if (status === 403
        && /laboratory endpoint/i.test(error.response?.data?.detail || '')
        && !window.location.pathname.startsWith('/portal')) {
      window.location.replace('/portal');
      return new Promise(() => {});   // never settles: the page is leaving
    }

    if (status === 401 && !original._retry && !isAuthCall && localStorage.getItem('refresh_token')) {
      original._retry = true;
      try {
        const newAccess = await _refreshAccessToken();
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newAccess}` };
        return api(original);
      } catch (refreshErr) {
        // THE SAME RULE THE 401 HANDLER BELOW ALREADY APPLIES: a failure is
        // not proof the session died. It was not applied here, so a refresh
        // that failed because the API was restarting cleared the token and
        // signed the user out mid-task.
        //
        // The session is over only when the server SAID so — a 4xx on
        // /auth/refresh means the refresh token is spent, revoked or
        // rejected — or when the refresh came back as somebody else, which
        // is worse than expiry and must not be papered over.
        const rs = refreshErr?.response?.status;
        const serverRefused = typeof rs === 'number' && rs >= 400 && rs < 500;
        const identityMoved = refreshErr?.message === 'refresh_identity_mismatch';
        // A view-as session that has run out puts you back in YOUR account,
        // not out of the application. The administrator's token was stashed
        // when the session began precisely so there is somewhere to return to.
        if (_isImpersonating()) {
          const mine = localStorage.getItem('impersonator_token');
          if (mine) {
            localStorage.setItem('token', mine);
            localStorage.removeItem('impersonator_token');
            localStorage.removeItem('user');
            // Not `/` — that is the public website here, not the application.
            window.location.href = '/dashboard';
            return Promise.reject(error);
          }
        }
        if (serverRefused || identityMoved) {
          clearAuthToken();
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        return Promise.reject(error);
      }
    }

    if (status === 401) {
      // A 401 from ONE endpoint is not proof the session died, and treating it
      // as proof is how clicking a single tab signed people out: the patient
      // Overview panel calls the portal endpoints, those refuse an account
      // flagged inactive, and this handler threw the whole session away on the
      // strength of it.
      //
      // Only clear when there is no evidence the session is alive. There are
      // two kinds of evidence:
      //   * `_retry` — we just refreshed the token successfully and the
      //     endpoint still said no. The session is demonstrably fine; the
      //     resource is not ours.
      //   * an access token whose own expiry is still in the future.
      // Otherwise the caller gets the error to handle, which is what every
      // panel here already does.
      //
      // Evidence outranks both: when the server sets X-Session-Ended the
      // session really is over — the token names nobody, or an admin changed
      // the account and invalidated every token issued before. Without this
      // the local expiry check wins, the app keeps a token the server will
      // never accept again, and the user sits on a page where every panel
      // loaded nothing and nothing explains why.
      const serverEndedSession =
        String(error.response?.headers?.['x-session-ended'] || '') === '1';
      const sessionLooksAlive = !serverEndedSession && (original._retry || (() => {
        const t = localStorage.getItem('token');
        if (!t) return false;
        try {
          const p = JSON.parse(atob(t.split('.')[1]));
          return !p.exp || p.exp * 1000 > Date.now();
        } catch { return false; }
      })());
      if (!sessionLooksAlive) {
        clearAuthToken();
        window.dispatchEvent(new Event('auth:unauthorized'));
        // Signing somebody out is only half the job. Dropping the user object
        // leaves the router on a route the anonymous tree does not have, so it
        // falls through to /home — the SPA's stale copy of a page the server
        // actually renders, which paints nothing. Go to the login form, and say
        // why. Only for the server's own verdict: an expired local token still
        // takes the quiet path, since that is the ordinary end of a long day.
        if (serverEndedSession && !window.location.pathname.startsWith('/login')) {
          window.location.replace('/login?session=ended');
          return new Promise(() => {});   // never settles: the page is leaving
        }
      }
    }
    if (status === 403) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            clearAuthToken();
            window.dispatchEvent(new Event('auth:unauthorized'));
          }
        } catch {}
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  
  register: (userData) =>
    api.post('/auth/register', userData),
  
  googleLogin: (token) =>
    api.post('/auth/google', { token }),
  
  getCurrentUser: (config) =>
    api.get('/users/me', config),
};

export const patientsAPI = {
  // Ask for the server's maximum. Sending no limit took the endpoint's
  // default of 100, so a page-size of 500 would have shown 100 rows and
  // called it everything.
  list: (params = {}) => api.get('/patients', { params: { limit: 500, ...params } }),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.patch(`/patients/${id}`, data),
  remove: (id) => api.delete(`/patients/${id}`),
  search: (query) => api.get('/patients', { params: { search: query } }).then(r => ({ data: { results: r.data } })),
};

export const insuranceAPI = {
  list: (params = {}) => api.get('/api/v1/insurances', { params }),
  create: (data) => api.post('/api/v1/insurances', data),
  update: (id, data) => api.patch(`/api/v1/insurances/${id}`, data),
  remove: (id) => api.delete(`/api/v1/insurances/${id}`),
};

// Patient Portal API
// Only emit ?patient_id= when there actually is one: on /patient the server
// resolves the chart from the login, and "patient_id=undefined" is either
// ignored (misleading in logs) or a 422 on the int-typed AI routes.
const pq = (id, sep = '?') =>
  (id === undefined || id === null || id === '') ? '' : `${sep}patient_id=${id}`;

export const patientPortalAPI = {
  getDashboard: (patientId) =>
    api.get(`/api/patient-portal/dashboard${pq(patientId)}`),
  
  getResults: (patientId) =>
    api.get(`/api/patient-portal/results${pq(patientId)}`),
  
  getResultInterpretation: (patientId, resultId) =>
    api.get(`/api/patient-portal/result/${resultId}/interpretation${pq(patientId)}`),
  
  getTrends: (patientId, analyteId) =>
    api.get(`/api/patient-portal/results/trends/${analyteId}${pq(patientId)}`),
  
  // The route is /ai/health-summary. Without the /ai segment this 404'd on
  // every call, which the dashboard swallowed into an empty card.
  getHealthSummary: (patientId) =>
    api.get(`/api/patient-portal/ai/health-summary${pq(patientId)}`),
  
  getInvoices: (patientId) =>
    api.get(`/api/patient-portal/invoices${pq(patientId)}`),
  
  makePayment: (patientId, data) =>
    api.post(`/api/patient-portal/payment${pq(patientId)}`, data),
};

// Client Portal API
export const clientPortalAPI = {
  getDashboard: (organizationId) =>
    api.get(`/api/client-portal/dashboard?organization_id=${organizationId}`),
  
  searchPatients: (organizationId, query) =>
    api.get(`/api/client-portal/patients?organization_id=${organizationId}&query=${query}`),
  
  getPatient: (organizationId, patientId) =>
    api.get(`/api/client-portal/patient/${patientId}?organization_id=${organizationId}`),
  
  placeOrder: (organizationId, data) =>
    api.post(`/api/client-portal/order?organization_id=${organizationId}`, data),
  
  getOrders: (organizationId) =>
    api.get(`/api/client-portal/orders?organization_id=${organizationId}`),
  
  getFinancials: (organizationId, startDate, endDate) =>
    api.get(`/api/client-portal/financials?organization_id=${organizationId}&start_date=${startDate}&end_date=${endDate}`),
  
  getAnalytics: (organizationId) =>
    api.get(`/api/client-portal/analytics?organization_id=${organizationId}`),
};

// Lab Portal API
export const labPortalAPI = {
  getDashboard: () =>
    api.get('/api/lab-portal/dashboard'),
  
  getOrders: (status, testType) =>
    api.get(`/api/lab-portal/orders?status=${status || ''}&test_type=${testType || ''}`),
  
  getPendingResults: () =>
    api.get('/api/lab-portal/results-pending'),
  
  enterResult: (resultId, value) =>
    api.post(`/api/lab-portal/result/${resultId}/enter-value`, { value }),
  
  getQCStatus: () =>
    api.get('/api/lab-portal/qc'),
  
  recordQCResult: (qcId, data) =>
    api.post(`/api/lab-portal/qc/${qcId}/record-result`, data),
  
  getTemperatureLogs: () =>
    api.get('/api/lab-portal/temperature'),
  
  getInventory: () =>
    api.get('/api/lab-portal/inventory'),
  
  getComplianceReport: (startDate, endDate) =>
    api.get(`/api/lab-portal/compliance-report?start_date=${startDate}&end_date=${endDate}`),
};

// AI Intelligence API
export const aiAPI = {
  // Executive AI
  getExecutiveDashboard: () =>
    api.get('/api/ai/executive/dashboard'),

  getWeeklyRecap: () =>
    api.get('/api/ai/executive/weekly-recap'),

  getMonthlyRecap: () =>
    api.get('/api/ai/executive/monthly-recap'),

  queryNLP: (query) =>
    api.post('/api/ai/executive/get-recap', { query }),

  // Lab AI
  getReagentAnomalies: (days = 30) =>
    api.get(`/api/ai/lab/reagent-anomalies?days=${days}`),

  getInstrumentDowntime: (days = 30) =>
    api.get(`/api/ai/lab/instrument-downtime?days=${days}`),

  // RCM AI
  getUnpaidClaims: (days = 90) =>
    api.get(`/api/ai/rcm/unpaid-claims?days=${days}`),

  // Patient AI
  getPatientTrends: (patientId, analyteId, days = 90) =>
    api.get(`/api/ai/patient/${patientId}/analyte/${analyteId}/trends?days=${days}`),

  getResultInterpretation: (patientId, resultId) =>
    api.get(`/api/ai/patient/${patientId}/result/${resultId}/interpretation`),

  getPatientHealthSummary: (patientId) =>
    api.get(`/api/ai/patient/${patientId}/health-summary`),
};

// Reporting API
export const reportingAPI = {
  getLabOperations: (startDate, endDate) =>
    api.get(`/api/reports/lab-operations?start_date=${startDate}&end_date=${endDate}`),
  
  getRevenue: (startDate, endDate) =>
    api.get(`/api/reports/revenue?start_date=${startDate}&end_date=${endDate}`),
  
  getDenials: (startDate, endDate) =>
    api.get(`/api/reports/denials?start_date=${startDate}&end_date=${endDate}`),
  
  getTestUtilization: (startDate, endDate) =>
    api.get(`/api/reports/test-utilization?start_date=${startDate}&end_date=${endDate}`),
  
  exportPDF: (reportType, params) =>
    api.get(`/api/reports/export/pdf?report_type=${reportType}`, { 
      params,
      responseType: 'blob' 
    }),
};

// Appointments API
export const appointmentsAPI = {
  getAvailableSlots: (date, location) =>
    api.get(`/api/appointments/available-slots?date=${date}&location=${location}`),
  
  createAppointment: (data) =>
    api.post('/api/appointments/', data),
  
  getPatientAppointments: (patientId) =>
    api.get(`/api/appointments/${patientId}`),
  
  reschedule: (appointmentId, data) =>
    api.put(`/api/appointments/${appointmentId}`, data),
  
  cancel: (appointmentId) =>
    api.delete(`/api/appointments/${appointmentId}`),
  
  checkIn: (appointmentId) =>
    api.post(`/api/appointments/${appointmentId}/check-in`),
};

// Ecommerce API
export const ecommerceAPI = {
  // Store catalog
  getProducts: (params = {}) => api.get('/store/products', { params }),
  getProduct: (id) => api.get(`/store/products/${id}`),
  getCategories: () => api.get('/store/categories'),
  getFeatured: () => api.get('/store/featured'),
  getBestsellers: () => api.get('/store/bestsellers'),

  // Cart (guest + authenticated)
  getCart: () => api.get('/cart', { headers: { 'X-Session-ID': getStoreSessionId() } }),
  addToCart: (data) => api.post('/cart/items', data, { headers: { 'X-Session-ID': getStoreSessionId() } }),
  updateCartItem: (itemId, data) => api.put(`/cart/items/${itemId}`, data, { headers: { 'X-Session-ID': getStoreSessionId() } }),
  removeCartItem: (itemId) => api.delete(`/cart/items/${itemId}`, { headers: { 'X-Session-ID': getStoreSessionId() } }),
  applyPromoCode: (promoCode) => api.post('/cart/apply-promo', { promo_code: promoCode }, { headers: { 'X-Session-ID': getStoreSessionId() } }),
  clearCart: () => api.delete('/cart/clear', { headers: { 'X-Session-ID': getStoreSessionId() } }),

  // Checkout
  checkout: (payload) => api.post('/checkout/process', payload),

  // Patient store portal
  getMyOrders: () => api.get('/patient/store/orders'),
  getMyOrder: (orderId) => api.get(`/patient/store/orders/${orderId}`),
  getMyPaymentMethods: () => api.get('/patient/store/payment-methods'),
  removeMyPaymentMethod: (id) => api.delete(`/patient/store/payment-methods/${id}`),
  getMySubscriptions: () => api.get('/patient/store/subscriptions'),

  // Subscriptions
  pauseSubscription: (id, reason) => api.put(`/subscriptions/${id}/pause`, null, { params: { reason } }),
  resumeSubscription: (id) => api.put(`/subscriptions/${id}/resume`),
  cancelSubscription: (id, reason) => api.put(`/subscriptions/${id}/cancel`, null, { params: { reason } }),
  skipSubscription: (id) => api.put(`/subscriptions/${id}/skip-next`),

  // Admin product management
  adminListProducts: () => api.get('/admin/products'),
  adminCreateProduct: (payload) => api.post('/admin/products', payload),
  adminPublishProduct: (id) => api.post(`/admin/products/${id}/publish`),
  adminUnpublishProduct: (id) => api.post(`/admin/products/${id}/unpublish`),
  adminDeleteProduct: (id) => api.delete(`/admin/products/${id}`),

  // Admin fulfillment
  adminPendingFulfillment: () => api.get('/admin/fulfillment/pending'),
  adminListFulfillment: (params = {}) => api.get('/admin/fulfillment', { params }),
  adminCreateFulfillment: (storeOrderId, warehouseId) =>
    api.post(`/admin/fulfillment/${storeOrderId}/create`, null, { params: { warehouse_id: warehouseId } }),
  adminPick: (fulfillmentId) => api.post(`/admin/fulfillment/${fulfillmentId}/pick`),
  adminCompletePick: (fulfillmentId) => api.post(`/admin/fulfillment/${fulfillmentId}/complete-pick`),
  adminPack: (fulfillmentId) => api.post(`/admin/fulfillment/${fulfillmentId}/pack`),
  adminShip: (fulfillmentId, trackingNumber, shippingCost = 0) =>
    api.post(`/admin/fulfillment/${fulfillmentId}/ship`, null, { params: { tracking_number: trackingNumber, shipping_cost: shippingCost } }),
  listWarehouses: () => api.get('/admin/fulfillment/warehouses'),

  // Admin shipments
  adminListShipments: (params = {}) => api.get('/shipments/admin/all', { params }),
  shipmentWebhook: (payload) => api.post('/shipments/webhooks/carriers', payload),

  // LIS entities for product creation
  listTests: () => api.get('/tests'),
  listPanels: () => api.get('/panels'),
  listBundles: () => api.get('/bundles'),
};

// ─── Microbiology ────────────────────────────────────────────────────────
export const microAPI = {
  // dictionaries
  listOrganisms: (params = {}) => api.get('/micro/organisms', { params }),
  // The smear / colony / growth dictionary, scoped to one culture's
  // specimen category so two growth scales never appear together.
  listPicklists: (params = {}) => api.get('/micro/picklists', { params }),
  listBreakpoints: (params = {}) => api.get('/micro/breakpoints', { params }),
  createBreakpoint: (data) => api.post('/micro/breakpoints', data),
  updateBreakpoint: (id, data) => api.patch(`/micro/breakpoints/${id}`, data),
  createPicklistValue: (data) => api.post('/micro/picklists', data),
  updatePicklistValue: (id, data) => api.patch(`/micro/picklists/${id}`, data),
  createOrganism: (data) => api.post('/micro/organisms', data),
  updateOrganism: (id, data) => api.patch(`/micro/organisms/${id}`, data),
  exportOrganisms: (params = {}) => api.get('/micro/organisms/export', { params, responseType: 'blob' }),
  importOrganisms: (formData) => api.post('/micro/organisms/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportDictionaries: (params = {}) => api.get('/micro/dictionaries/export.xlsx', { params, responseType: 'blob' }),
  importDictionaries: (formData) => api.post('/micro/dictionaries/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listAntibiotics: (params = {}) => api.get('/micro/antibiotics', { params }),
  createAntibiotic: (data) => api.post('/micro/antibiotics', data),
  updateAntibiotic: (id, data) => api.patch(`/micro/antibiotics/${id}`, data),
  listPresets: (params = {}) => api.get('/micro/presets', { params }),
  createPreset: (data) => api.post('/micro/presets', data),
  updatePreset: (id, data) => api.patch(`/micro/presets/${id}`, data),
  listColonyCounts: (params = {}) => api.get('/micro/colony-counts', { params }),
  createColonyCount: (data) => api.post('/micro/colony-counts', data),
  updateColonyCount: (id, data) => api.patch(`/micro/colony-counts/${id}`, data),
  listResistanceMarkers: (params = {}) => api.get('/micro/resistance-markers', { params }),
  createResistanceMarker: (data) => api.post('/micro/resistance-markers', data),
  updateResistanceMarker: (id, data) => api.patch(`/micro/resistance-markers/${id}`, data),
  listBreakpoints: (params = {}) => api.get('/micro/breakpoints', { params }),
  // batches
  listBatches: (params = {}) => api.get('/micro/batches', { params }),
  createBatch: (data) => api.post('/micro/batches', data),
  // cultures / worklist
  listCultures: (params = {}) => api.get('/micro/cultures', { params }),
  getCulture: (id) => api.get(`/micro/cultures/${id}`),
  createCulture: (data) => api.post('/micro/cultures', data),
  scanCultures: (data) => api.post('/micro/cultures/scan', data),
  updateCulture: (id, data) => api.patch(`/micro/cultures/${id}`, data),
  applyPreset: (data) => api.post('/micro/cultures/apply-preset', data),
  reportCulture: (id, data) => api.post(`/micro/cultures/${id}/report`, data),
  // isolates
  addIsolate: (cid, data) => api.post(`/micro/cultures/${cid}/isolates`, data),
  updateIsolate: (iid, data) => api.patch(`/micro/isolates/${iid}`, data),
  deleteIsolate: (iid) => api.delete(`/micro/isolates/${iid}`),
  // susceptibilities
  addSusc: (iid, data) => api.post(`/micro/isolates/${iid}/susceptibilities`, data),
  bulkSusc: (iid, data) => api.post(`/micro/isolates/${iid}/susceptibilities/bulk`, data),
  updateSusc: (sid, data) => api.patch(`/micro/susceptibilities/${sid}`, data),
  deleteSusc: (sid) => api.delete(`/micro/susceptibilities/${sid}`),
  // summary
  worklistSummary: (params = {}) => api.get('/micro/worklist/summary', { params }),
  // MDRO alerts — a resistant isolate nobody has looked at yet
  listMdro: (params = {}) => api.get('/micro/mdro', { params }),
  acknowledgeMdro: (id, data = {}) => api.post(`/micro/mdro/${id}/acknowledge`, data),
  // antibiogram
  getAntibiogram: (params = {}) => api.get('/micro/antibiogram', { params }),
  antibiogramPdf: (params = {}) => api.get('/micro/antibiogram',
    { params: { ...params, fmt: 'pdf' }, responseType: 'blob' }),
  listAntibiogramSubs: () => api.get('/micro/antibiogram/subscriptions'),
  createAntibiogramSub: (data) => api.post('/micro/antibiogram/subscriptions', data),
  updateAntibiogramSub: (id, data) => api.patch(`/micro/antibiogram/subscriptions/${id}`, data),
  deleteAntibiogramSub: (id) => api.delete(`/micro/antibiogram/subscriptions/${id}`),
  sendAntibiogramSub: (id) => api.post(`/micro/antibiogram/subscriptions/${id}/send`),
};

// ─── Reference Labs / Send-Outs ────────────────────────────────────────────
export const sendoutsAPI = {
  // performing-lab registry
  listLabs: () => api.get('/api/sendouts/labs'),
  // Panels and payers are the two new things a routing rule can be scoped
  // to. Payers are searched rather than listed: the directory runs to
  // thousands, and a dropdown of all of them is a dropdown nobody can use.
  listPanelsForRouting: () => api.get('/panels'),
  searchPayers: (search) =>
    api.get('/insurance-payers/', { params: { search, active_only: true } }),
  // Referral agreements — both sides of one can read it, each signs its own.
  listAgreements: () => api.get('/api/sendouts/agreements'),
  getAgreement: (id) => api.get(`/api/sendouts/agreements/${id}`),
  signAgreement: (id, data) => api.post(`/api/sendouts/agreements/${id}/sign`, data),
  // Starting and ending a connection with another laboratory.
  listConnections: () => api.get('/api/sendouts/connections'),
  requestConnection: (data) => api.post('/api/sendouts/connections', data),
  acceptConnection: (id) => api.post(`/api/sendouts/connections/${id}/accept`),
  declineConnection: (id) => api.post(`/api/sendouts/connections/${id}/decline`),
  disconnect: (partnershipId) => api.delete(`/api/sendouts/partnerships/${partnershipId}`),
  createLab: (data) => api.post('/api/sendouts/labs', data),
  updateLab: (id, data) => api.put(`/api/sendouts/labs/${id}`, data),
  // routing rules
  listRules: (params = {}) => api.get('/api/sendouts/routing-rules', { params }),
  saveRule: (data) => api.post('/api/sendouts/routing-rules', data),
  deleteRule: (id) => api.delete(`/api/sendouts/routing-rules/${id}`),
  updateRule: (id, data) => api.put(`/api/sendouts/routing-rules/${id}`, data),
  reactivateRule: (id) => api.post(`/api/sendouts/routing-rules/${id}/reactivate`),
  deleteRuleForGood: (id) => api.delete(`/api/sendouts/routing-rules/${id}/permanently`),
  exportRules: (fmt = 'xlsx') =>
    api.get('/api/sendouts/routing-rules/export', { params: { fmt }, responseType: 'blob' }),
  importRules: (payload, apply) =>
    api.post('/api/sendouts/routing-rules/import', { ...payload, apply }),
  // pricing
  listPricing: (params = {}) => api.get('/api/sendouts/pricing', { params }),
  savePricing: (data) => api.post('/api/sendouts/pricing', data),
  deletePricing: (id) => api.delete(`/api/sendouts/pricing/${id}`),
  // markup rules (reference cost + markup %)
  listMarkupRules: (params = {}) => api.get('/api/sendouts/markup-rules', { params }),
  saveMarkupRule: (data) => api.post('/api/sendouts/markup-rules', data),
  deleteMarkupRule: (id) => api.delete(`/api/sendouts/markup-rules/${id}`),
  // send-out orders
  listOrders: (params = {}) => api.get('/api/sendouts/orders', { params }),
  inbound: (performingLabId, params = {}) =>
    api.get('/api/sendouts/inbound', { params: { performing_lab_id: performingLabId, ...params } }),
  getOrder: (id) => api.get(`/api/sendouts/orders/${id}`),
  action: (id, action, data = {}) => api.post(`/api/sendouts/orders/${id}/action/${action}`, data),
  enterResults: (id, data) => api.post(`/api/sendouts/orders/${id}/enter-results`, data),
  createPerformingOrder: (id) => api.post(`/api/sendouts/orders/${id}/performing-order`),
  returnResults: (id) => api.post(`/api/sendouts/orders/${id}/return-results`),
  // Scan the barcode that is physically ON the arriving tube. One call:
  // identify it, receive it, mint our order, hand back OUR accession.
  scanInbound: (code, performingLabId, receive = true) =>
    api.post('/api/sendouts/inbound/scan',
             { code, performing_lab_id: performingLabId || null, receive }),
  // Our label for a tube wearing someone else's barcode.
  specimenLabelUrl: (sendoutId) =>
    `/api/sendouts/orders/${sendoutId}/specimen-label.pdf`,
  specimenLabel: (sendoutId) =>
    api.get(`/api/sendouts/orders/${sendoutId}/specimen-label.pdf`,
            { responseType: 'blob' }),
  // The label the ORDERING lab's bench puts on the tube before it ships:
  // our patient, their accession. Available once the send-out is marked
  // sent, which is what reserves their number.
  partnerLabel: (sendoutId) =>
    api.get(`/api/sendouts/orders/${sendoutId}/partner-label.pdf`,
            { responseType: 'blob' }),
  transmit: (id) => api.post(`/api/sendouts/orders/${id}/transmit`),
  // paperwork PDFs (blob)
  requisitionPdf: (id) => api.get(`/api/sendouts/orders/${id}/requisition.pdf`, { responseType: 'blob' }),
  packingSlipPdf: (id) => api.get(`/api/sendouts/orders/${id}/packing-slip.pdf`, { responseType: 'blob' }),
  manifestPdf: (params = {}) => api.get('/api/sendouts/manifest.pdf', { params, responseType: 'blob' }),
  // supporting lookups
  listTests: () => api.get('/tests'),
  testAnalytes: (testId) => api.get(`/tests/${testId}/analytes`),
};

export default api;
